import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Logo } from '../../Logo';
const collageImages = ['https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=400&q=70', 'https://cdn.magicpatterns.com/uploads/wpmJem8TDq4uTdC2Nk1R42/618820481_18125169400552839_4998556993096379336_n.jpg', 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&q=70', 'https://images.unsplash.com/photo-1476224203421-9ac39bcb3327?w=400&q=70', 'https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=400&q=70', 'https://cdn.magicpatterns.com/uploads/trmyzxT7gGMiHEqKGydQyv/615938090_18081177796965646_3507185526629023982_n.jpg', 'https://images.unsplash.com/photo-1547592180-85f173990554?w=400&q=70', 'https://images.unsplash.com/photo-1519708227418-c8fd9a32b7a2?w=400&q=70', 'https://cdn.magicpatterns.com/uploads/aPvpJpC6db6KY2BRCQTVNT/620509937_18131320030506803_144782265458598508_n.jpg', 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=400&q=70', 'https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=400&q=70', 'https://cdn.magicpatterns.com/uploads/kiTpUwEkzUdsyvmpKR11cY/627172515_18336665800208534_7795684121705742258_n.jpg'];
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
export function Home() {
  return <div className="w-full" data-id="element-1688">
      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20" data-id="element-1689">
        {/* Background Collage */}
        <div className="absolute inset-0 z-0" data-id="element-1690">
          <div className="grid grid-cols-2 md:grid-cols-4 grid-rows-6 md:grid-rows-3 w-full h-full opacity-40" data-id="element-1691">
            {collageImages.map((src, i) => <div key={i} className="overflow-hidden" data-id="element-1692">
                <img src={src} alt="" className={`w-full h-full object-cover ${i === 1 ? 'object-[25%_center]' : ''}`} data-id="element-1693" />
              </div>)}
          </div>
          <div className="absolute inset-0 bg-charcoal/85" data-id="element-1694" />
          <div className="absolute inset-0 bg-gradient-to-b from-bodybg/50 via-transparent to-bodybg" data-id="element-1695" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center" data-id="element-1696">
          <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="flex flex-col items-center" data-id="element-1697">
            <motion.p variants={fadeUpVariants} className="text-[10px] md:text-xs font-bold text-[#9A9A90] uppercase tracking-[0.25em] mb-6" data-id="element-1698">
              Jack Achenbach · MS, RDN, CPT
            </motion.p>

            <motion.h1 variants={fadeUpVariants} className="text-5xl md:text-7xl lg:text-8xl font-serif text-white leading-[1.1] mb-8 max-w-4xl" data-id="element-1699">
              Structure provides the foundation for success.
            </motion.h1>

            <motion.div variants={fadeUpVariants} className="w-16 h-0.5 bg-tealslate mb-8" data-id="element-1700" />

            <motion.p variants={fadeUpVariants} className="text-lg md:text-xl text-[#D8D8D0] max-w-2xl mb-12 leading-relaxed" data-id="element-1701">
              Specialized nutrition coaching for metabolic health, GLP-1
              support, and sustainable muscle preservation.
            </motion.p>

            <motion.div variants={fadeUpVariants} className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto" data-id="element-1702">
              <Link to="/contact" className="w-full sm:w-auto px-8 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-lightteal transition-colors text-center" data-id="element-1703">
                Work With Jack
              </Link>
              <Link to="/books" className="w-full sm:w-auto px-8 py-4 border border-[#D8D8D0] text-[#D8D8D0] text-sm font-bold uppercase tracking-wider hover:bg-white/10 transition-colors text-center" data-id="element-1704">
                Browse Books
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.5,
        duration: 1
      }} className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2" data-id="element-1705">
          <span className="text-[10px] text-[#7A7A70] uppercase tracking-widest" data-id="element-1706">
            Scroll
          </span>
          <div className="w-[1px] h-12 bg-gradient-to-b from-[#7A7A70] to-transparent" data-id="element-1707" />
        </motion.div>
      </section>

      {/* INTRO BLURB */}
      <section className="bg-[#F5F5F0] text-[#1F1F1F] py-24 md:py-32 px-6" data-id="element-1708">
        <div className="max-w-7xl mx-auto" data-id="element-1709">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-start" data-id="element-1710">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeUpVariants} data-id="element-1711">
              <h2 className="text-4xl md:text-5xl font-serif text-[#1F1F1F] leading-tight mb-6" data-id="element-1712">
                Bridging the gap between medical intervention and practical,
                everyday nutrition.
              </h2>
              <div className="w-12 h-1 bg-tealslate" data-id="element-1713" />
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeUpVariants} className="space-y-6 text-base md:text-lg text-[#3B3B3B] leading-relaxed" data-id="element-1714">
              <p data-id="element-1715">
                Jack Achenbach is a Registered Dietitian Nutritionist (RDN) and
                Certified Personal Trainer (CPT) specializing in metabolic
                health and sustainable weight management.
              </p>
              <p data-id="element-1716">
                With extensive experience helping clients navigate the
                complexities of GLP-1 medications, Jack developed a framework to
                help protect muscle mass, optimize nutrient intake, and build
                habits that outlast the prescription.
              </p>
              <div className="pt-4" data-id="element-1717">
                <Link to="/about" className="inline-flex items-center gap-2 text-sm font-bold text-tealslate uppercase tracking-wider hover:text-navyslate transition-colors group" data-id="element-1718">
                  About Jack
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-1719" />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="bg-bodybg py-24 md:py-32 px-6 border-t border-darkgray" data-id="element-1720">
        <div className="max-w-7xl mx-auto" data-id="element-1721">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6" data-id="element-1722">
            <div data-id="element-1723">
              <p className="text-[10px] font-bold text-[#9A9A90] uppercase tracking-[0.25em] mb-4" data-id="element-1724">
                How we work together
              </p>
              <h2 className="text-4xl md:text-5xl font-serif text-white" data-id="element-1725">
                Core Services
              </h2>
            </div>
            <Link to="/services" className="inline-flex items-center gap-2 text-sm font-bold text-[#D8D8D0] uppercase tracking-wider hover:text-white transition-colors group" data-id="element-1726">
              View all services
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-1727" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1728">
            {[{
            title: '1:1 GLP-1 Nutrition Coaching',
            desc: 'Personalized guidance to maximize fat loss while minimizing side effects on medication.',
            label: 'Signature'
          }, {
            title: 'Body Composition & Muscle',
            desc: 'Targeted protein and training protocols to preserve lean mass during rapid weight loss.',
            label: 'Focus'
          }, {
            title: 'Transitioning Off Medication',
            desc: 'Exit strategies and habit building to maintain results when stepping down dosage.',
            label: 'Maintenance'
          }].map((service, i) => <motion.div key={i} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={{
            hidden: {
              opacity: 0,
              y: 20
            },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                delay: i * 0.1,
                duration: 0.6
              }
            }
          }} className="border-l-2 border-tealslate bg-navyslate p-8 flex flex-col h-full group hover:bg-bluegray transition-colors" data-id="element-1729">
                <span className="text-[10px] font-bold text-lightteal uppercase tracking-widest mb-4 block" data-id="element-1730">
                  {service.label}
                </span>
                <h3 className="text-2xl font-serif text-white mb-4" data-id="element-1731">
                  {service.title}
                </h3>
                <p className="text-[#D8D8D0] leading-relaxed mb-8 flex-1" data-id="element-1732">
                  {service.desc}
                </p>
                <Link to="/services" className="inline-flex items-center gap-2 text-xs font-bold text-white uppercase tracking-wider mt-auto" data-id="element-1733">
                  Learn more{' '}
                  <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" data-id="element-1734" />
                </Link>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* THE GUIDE FEATURE */}
      <section className="bg-[#F5F5F0] py-24 md:py-32 px-6" data-id="element-1735">
        <div className="max-w-7xl mx-auto" data-id="element-1736">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1737">
            {/* Left: Book Cover Mockup */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="relative mx-auto w-full max-w-md" data-id="element-1738">
              <div className="aspect-[3/4] bg-charcoal shadow-2xl p-8 md:p-12 flex flex-col relative overflow-hidden border border-darkgray" data-id="element-1739">
                <div className="absolute top-0 left-0 w-full h-2 bg-tealslate" data-id="element-1740" />
                <Logo className="w-12 h-12 mb-auto opacity-80" data-id="element-1741" />

                <div className="mt-auto" data-id="element-1742">
                  <p className="text-[10px] font-bold text-[#9A9A90] uppercase tracking-[0.25em] mb-4" data-id="element-1743">
                    Structured Nutrition
                  </p>
                  <h3 className="text-4xl md:text-5xl font-serif text-white leading-tight mb-4" data-id="element-1744">
                    The GLP-1
                    <br data-id="element-1745" />
                    Nutrition Guide
                  </h3>
                  <div className="w-8 h-0.5 bg-tealslate mb-4" data-id="element-1746" />
                  <p className="text-xs text-[#D8D8D0] uppercase tracking-widest" data-id="element-1747">
                    Jack Achenbach, MS, RDN
                  </p>
                </div>
              </div>
              {/* Decorative shadow/depth */}
              <div className="absolute -right-4 top-4 bottom-4 w-4 bg-darkgray transform skew-y-[45deg] origin-top-left -z-10 shadow-lg" data-id="element-1748" />
            </motion.div>

            {/* Right: Pitch & Capture */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-1749">
              <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-4" data-id="element-1750">
                Free Resource
              </p>
              <h2 className="text-4xl md:text-5xl font-serif text-[#1F1F1F] mb-6" data-id="element-1751">
                Navigate your medication with confidence.
              </h2>
              <p className="text-lg text-[#3B3B3B] mb-10 leading-relaxed" data-id="element-1752">
                A comprehensive framework for protecting muscle mass, managing
                side effects, and building sustainable habits while on GLP-1
                therapies. Download the complete 40+ page guide for free.
              </p>

              <form className="flex flex-col sm:flex-row gap-4 mb-6" onSubmit={e => e.preventDefault()} data-id="element-1753">
                <input type="email" placeholder="Enter your email address" className="flex-1 bg-white border border-[#D8D8D0] px-6 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" required data-id="element-1754" />
                <button type="submit" className="px-8 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-navyslate transition-colors whitespace-nowrap" data-id="element-1755">
                  Get the Guide
                </button>
              </form>

              <Link to="/booklet" className="inline-flex items-center gap-2 text-sm font-bold text-[#7A7A70] hover:text-[#1F1F1F] transition-colors" data-id="element-1756">
                Or read the full guide online <ArrowRight className="w-4 h-4" data-id="element-1757" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section className="bg-burgundy py-24 md:py-32 px-6 text-center" data-id="element-1758">
        <div className="max-w-4xl mx-auto" data-id="element-1759">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="flex flex-col items-center" data-id="element-1760">
            <Logo className="w-12 h-12 opacity-30 mb-8" data-id="element-1761" />
            <p className="text-3xl md:text-5xl font-serif text-white italic leading-relaxed mb-10" data-id="element-1762">
              "The medication gives you the opportunity. The structure gives you
              the results."
            </p>
            <div className="w-16 h-1 bg-tealslate" data-id="element-1763" />
          </motion.div>
        </div>
      </section>

      {/* TRAINING APP TEASER */}
      <section className="bg-bodybg py-24 md:py-32 px-6 border-t border-darkgray relative overflow-hidden" data-id="element-1764">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-tealslate/5 rounded-full blur-3xl" data-id="element-1765" />
        <div className="max-w-7xl mx-auto relative z-10" data-id="element-1766">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1767">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8
          }} data-id="element-1768">
              <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-4" data-id="element-1769">
                Structured Training App
              </p>
              <h2 className="text-4xl md:text-5xl font-serif text-white leading-tight mb-6" data-id="element-1770">
                Programmed workouts. In your pocket.
              </h2>
              <div className="w-12 h-0.5 bg-tealslate mb-6" data-id="element-1771" />
              <p className="text-base md:text-lg text-[#D8D8D0] leading-relaxed mb-8 max-w-xl" data-id="element-1772">
                Coach-built training designed to preserve muscle, build
                strength, and maximize your GLP-1 results. Video demos, progress
                tracking, and direct messaging — all for less than a coffee a
                week.
              </p>

              <div className="flex items-baseline gap-3 mb-8" data-id="element-1773">
                <span className="text-5xl font-serif text-white" data-id="element-1774">$6.99</span>
                <span className="text-sm text-[#9CA3AF] uppercase tracking-widest" data-id="element-1775">
                  / month
                </span>
              </div>

              <div className="flex flex-col sm:flex-row gap-4" data-id="element-1776">
                <Link to="/training" className="px-8 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-lightteal transition-colors text-center inline-flex items-center justify-center gap-2" data-id="element-1777">
                  Explore Training <ArrowRight className="w-4 h-4" data-id="element-1778" />
                </Link>
                <a href="https://www.trainerize.me/profile/structured/?planGUID=0adc7cc9d3274ca183f4a7ca2a0e16a3" target="_blank" rel="noopener noreferrer" className="px-8 py-4 border border-[#D8D8D0] text-[#D8D8D0] text-sm font-bold uppercase tracking-wider hover:bg-white/10 transition-colors text-center" data-id="element-1779">
                  Sign Up Now
                </a>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="flex justify-center" data-id="element-1780">
              <div className="relative w-[240px] md:w-[280px]" data-id="element-1781">
                <div className="relative bg-charcoal rounded-[2.5rem] p-2.5 shadow-2xl border-[3px] border-darkgray" data-id="element-1782">
                  <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-24 h-5 bg-charcoal rounded-b-2xl z-20" data-id="element-1783" />
                  <div className="bg-bodybg rounded-[2rem] overflow-hidden aspect-[9/19.5] p-4 pt-8" data-id="element-1784">
                    <div className="flex items-center justify-between mb-5 pt-2" data-id="element-1785">
                      <Logo className="w-6 h-6" data-id="element-1786" />
                      <span className="text-[7px] font-bold text-[#9A9A90] uppercase tracking-[0.25em]" data-id="element-1787">
                        Structured
                      </span>
                    </div>
                    <p className="text-[8px] text-tealslate uppercase tracking-widest mb-1 font-bold" data-id="element-1788">
                      Today's Workout
                    </p>
                    <h3 className="text-base font-serif text-white leading-tight mb-1" data-id="element-1789">
                      Upper Body
                    </h3>
                    <p className="text-[8px] text-[#9CA3AF] mb-4" data-id="element-1790">
                      Week 3 · Day 2 · 45 min
                    </p>
                    <div className="bg-darkgray h-0.5 rounded-full overflow-hidden mb-4" data-id="element-1791">
                      <div className="bg-tealslate h-full w-[35%] rounded-full" data-id="element-1792" />
                    </div>
                    <div className="space-y-1.5" data-id="element-1793">
                      {['Incline DB Press', 'Cable Row', 'Lat Pulldown', 'Lateral Raise'].map((name, i) => <div key={i} className={`flex items-center gap-2 p-2 rounded ${i === 2 ? 'bg-tealslate/20 border-l-2 border-tealslate' : 'bg-navyslate/30'} ${i < 2 ? 'opacity-60' : ''}`} data-id="element-1794">
                          <div className={`w-3.5 h-3.5 rounded-full shrink-0 ${i < 2 ? 'bg-tealslate' : 'border border-[#9CA3AF]'}`} data-id="element-1795" />
                          <p className="text-[8px] font-bold text-white truncate flex-1" data-id="element-1796">
                            {name}
                          </p>
                        </div>)}
                    </div>
                  </div>
                </div>
                <div className="absolute inset-0 bg-tealslate/10 blur-3xl -z-10" data-id="element-1797" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA FOOTER BAND */}
      <section className="bg-charcoal py-24 px-6 text-center border-t border-darkgray" data-id="element-1798">
        <div className="max-w-2xl mx-auto" data-id="element-1799">
          <h2 className="text-4xl md:text-5xl font-serif text-white mb-8" data-id="element-1800">
            Ready to build your structure?
          </h2>
          <Link to="/contact" className="inline-flex items-center justify-center px-10 py-5 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-lightteal transition-colors" data-id="element-1801">
            Book a Discovery Call
          </Link>
        </div>
      </section>
    </div>;
}